import { clearPrivateLocalData, deleteMeta, getMeta, setMeta } from '../offline/db';
import { syncMutationQueue } from '../offline/queue';

export type ClientUser = {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
};

type AuthResponse = {
  user: ClientUser;
  offlineAuthorizedUntil: string;
};

export async function setupAdmin(input: {
  displayName: string;
  email: string;
  password: string;
  bootstrapToken: string;
}): Promise<AuthResponse> {
  const response = await fetch('/api/setup', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input)
  });
  if (!response.ok) throw new Error('SETUP_FAILED');
  const data = (await response.json()) as AuthResponse;
  await acceptVerifiedSession(data.offlineAuthorizedUntil);
  void syncMutationQueue();
  return data;
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!response.ok) throw new Error('LOGIN_FAILED');
  const data = (await response.json()) as AuthResponse;
  await acceptVerifiedSession(data.offlineAuthorizedUntil);
  void syncMutationQueue();
  return data;
}

export async function fetchCurrentUser(): Promise<ClientUser | null> {
  if (await getMeta<boolean>('forcedSignedOut')) return null;
  try {
    const response = await fetch('/api/auth/me', { credentials: 'include' });
    if (response.status === 401 || response.status === 403) {
      await clearPrivateLocalData();
      return null;
    }
    if (!response.ok) return null;
    const data = (await response.json()) as {
      user: ClientUser;
      offlineAuthorizedUntil: string;
    };
    await acceptVerifiedSession(data.offlineAuthorizedUntil);
    await setMeta('lastVerifiedUser', data.user);
    return data.user;
  } catch {
    const until = await getMeta<string>('offlineAuthorizedUntil');
    if (!until || Date.parse(until) <= Date.now()) return null;
    return await getMeta<ClientUser>('lastVerifiedUser');
  }
}

export async function rememberVerifiedUser(user: ClientUser) {
  await setMeta('lastVerifiedUser', user);
}

async function acceptVerifiedSession(offlineAuthorizedUntil: string) {
  await setMeta('offlineAuthorizedUntil', offlineAuthorizedUntil);
  await setMeta('forcedSignedOut', false);
  await deleteMeta('pendingServerLogout');
}

export async function logout(): Promise<void> {
  if (!navigator.onLine) {
    await setMeta('pendingServerLogout', true);
    await setMeta('forcedSignedOut', true);
    await clearPrivateLocalData({ keepPendingLogout: true });
    return;
  }

  try {
    const response = await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    if (!response.ok && response.status !== 204) throw new Error('LOGOUT_FAILED');
    await clearPrivateLocalData();
  } catch {
    // Treat a transport failure exactly like an offline logout: local access ends
    // immediately, while server revocation is retried after connectivity recovers.
    await setMeta('pendingServerLogout', true);
    await setMeta('forcedSignedOut', true);
    await clearPrivateLocalData({ keepPendingLogout: true });
  }
}

export async function flushPendingServerLogout(): Promise<boolean> {
  const pending = await getMeta<boolean>('pendingServerLogout');
  if (!pending || !navigator.onLine) return false;

  try {
    const response = await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    if (!response.ok && response.status !== 204) return false;
    await clearPrivateLocalData();
    return true;
  } catch {
    return false;
  }
}
